import React from 'react';
import Package from 'lucide-react/dist/esm/icons/package';

import PalletCard from 'components/logistics/fulfillmentLifecycle/modals/tabs/PalletCard';
import { Alert, AlertDescription, AlertTitle } from 'shadcn/components/ui/Alert';
import { Skeleton } from 'shadcn/components/ui/Skeleton';

/**
 * Number of skeleton cards to show during loading.
 * Hoisted to module scope to avoid re-creation on each render.
 */
const SKELETON_COUNT = [1, 2, 3];

/**
 * PalletsTab
 *
 * Container component for the Pallets tab in the RouteDetailModal.
 * Renders a responsive grid of PalletCard components, a loading skeleton,
 * or an empty state message.
 *
 * @param {Object} props
 * @param {Array} [props.pallets] - Array of pallet objects from the breakdown API
 * @param {boolean} [props.loading] - Whether the breakdown data is still loading
 * @param {string|null} [props.error] - Error message when pallet breakdown request fails
 */
function PalletsTab({ error = null, loading = false, pallets }) {
    if (loading) {
        return (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 py-4" data-testid="pallets-loading">
                {SKELETON_COUNT.map((key) => (
                    <Skeleton key={key} className="h-32 rounded-xl" />
                ))}
            </div>
        );
    }

    if (error) {
        return (
            <Alert variant="destructive" data-testid="pallets-error">
                <AlertTitle>Error loading pallets</AlertTitle>
                <AlertDescription>{error}</AlertDescription>
            </Alert>
        );
    }

    const hasPallets = Array.isArray(pallets) && pallets.length > 0;

    return hasPallets ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 py-4" data-testid="pallets-grid">
            {pallets.map((pallet) => (
                <PalletCard key={pallet.palletId} pallet={pallet} />
            ))}
        </div>
    ) : (
        <div className="py-8 text-center" data-testid="pallets-empty">
            <Package className="mx-auto h-8 w-8 text-muted-foreground mb-2" />
            <p className="text-sm text-muted-foreground">No pallets assigned to this route yet.</p>
        </div>
    );
}

export default PalletsTab;
